import { CrudForm } from '../shared/CrudPage';
import { bankTransactionService } from '@kss/api';
import type { BankTransaction, BankTransactionCreate, BankTransactionFilter } from '@kss/api';
import { loadStoreOptions, loadAccountOptions } from '../shared/formLoaders';

/**
 * BankTransaction form — wired to the live schema verified
 * 2026-05-05. Live row uses `txnDate`, `bankAccountId`,
 * `referenceNumber`, `description` (no separate `reference` column),
 * and `type` is the SQL-style upper-case literal `"CREDIT" | "DEBIT"`.
 * The strict-mode JSON binder rejects the legacy keys
 * (`transactionDate`, `accountId`, `reference`, `isActive`) with
 * `UNKNOWN_FIELD`. Read side still accepts the legacy keys via the
 * data-type aliases.
 */
type F = {
  txnDate: string;
  amount: number;
  type: 'CREDIT' | 'DEBIT';
  referenceNumber: string;
  description: string;
  bankAccountId: string;
  storeId: string;
  isReconciled: boolean;
};
const today = new Date().toISOString().slice(0, 10);
const empty: F = {
  txnDate: today,
  amount: 0,
  type: 'CREDIT',
  referenceNumber: '',
  description: '',
  bankAccountId: '',
  storeId: '',
  isReconciled: false,
};

export default function NewBankTransaction() {
  return (
    <CrudForm<BankTransaction, BankTransactionCreate, BankTransactionFilter, F>
      title="bank transaction"
      service={bankTransactionService}
      idKey="bankTransactionId"
      basePath="/panel/bank-transactions"
      emptyForm={empty}
      fields={[
        { key: 'txnDate', label: 'Transaction date', type: 'date', required: true },
        { key: 'amount', label: 'Amount (₹)', type: 'number', required: true },
        {
          key: 'type',
          label: 'Type',
          type: 'select',
          options: [
            { value: 'DEBIT', label: 'Debit' },
            { value: 'CREDIT', label: 'Credit' },
          ],
        },
        { key: 'referenceNumber', label: 'Reference number', placeholder: 'Bank txn ref' },
        { key: 'bankAccountId', label: 'Bank account', type: 'search', loadOptions: loadAccountOptions, placeholder: 'Pick an account' },
        { key: 'storeId', label: 'Store', type: 'search', loadOptions: loadStoreOptions, placeholder: 'Select a store' },
        { key: 'isReconciled', label: 'Reconciled', type: 'status' },
        { key: 'description', label: 'Description', type: 'textarea', fullWidth: true },
      ]}
      fromEntity={(r) => ({
        // Read either the live or legacy field name — older saved
        // rows from before this rebind still resolve via aliases on
        // the BankTransaction interface.
        txnDate: ((r.txnDate ?? r.transactionDate) ?? `${today}T00:00:00`).slice(0, 10),
        amount: Number(r.amount ?? 0),
        type: ((r.type ?? 'CREDIT').toString().toUpperCase() === 'DEBIT'
          ? 'DEBIT'
          : 'CREDIT') as 'CREDIT' | 'DEBIT',
        referenceNumber: r.referenceNumber ?? r.reference ?? '',
        description: r.description ?? '',
        bankAccountId: r.bankAccountId ?? r.accountId ?? '',
        storeId: r.storeId ?? '',
        isReconciled: !!r.isReconciled,
      })}
      toEntity={(f, id) => ({
        bankTransactionId: id,
        txnDate: f.txnDate ? `${f.txnDate}T00:00:00` : undefined,
        amount: f.amount,
        type: f.type,
        referenceNumber: f.referenceNumber || undefined,
        description: f.description || undefined,
        bankAccountId: f.bankAccountId || undefined,
        storeId: f.storeId || undefined,
        isReconciled: f.isReconciled,
      })}
      validate={(f) => (f.amount <= 0 ? 'Amount must be > 0' : null)}
    />
  );
}
